import { useCallback, useEffect, useState, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { Box, useTheme } from '@mui/material'
import { DndContext, type DragEndEvent } from '@dnd-kit/core'

// Contexts
import { FileProvider } from '@/contexts/FileContext'
import { ChartConfigProvider, useChartConfig } from '@/contexts/ChartConfigContext'
import { TemplateProvider, useTemplates } from '@/contexts/TemplateContext'
import { TimeShiftProvider, useTimeShifts } from '@/contexts/TimeShiftContext'
import { useFiles } from '@/contexts/FileContext'

// Components
import { WaveSidebar } from '@/components/wave-sidebar'
import { FileExplorer } from '@/components/files/FileExplorer/FileExplorer'
import { CurveExplorer } from '@/components/curves/CurveExplorer/CurveExplorer'
import { ChartConfigurator } from '@/components/charts/ChartConfigurator'
import { TimeShiftPanel } from '@/components/timeshift/TimeShiftPanel'
import TemplateDropdown from '@/components/templates/TemplateDropdown'
import { ChartDataResolver } from '@/components/charts/WaveChartCanvas/Services/ChartDataResolver'
import { assignDefaultCurveStyling } from '@/components/charts/WaveChartCanvas/Services/CurveStyling'
import { WaveChartCanvas } from '@/components/charts'

// Icons
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import DataUsageIcon from '@mui/icons-material/DataUsage'
import BarChartIcon from '@mui/icons-material/BarChart'
import AccessTimeIcon from '@mui/icons-material/AccessTime'

// Types
import type { StyledCurveReference, ChartConfig, YAxisPosition, YAxisConfig, CurveStyling, ChartHeightPreset } from '@/interfaces/waveChart'
import type { ToolType } from '@/interfaces/toolData'
import type { SidebarFile } from '@/interfaces/sidebar'
import type { WaveFile } from '@/contexts/FileContext'

/**
 * Convert WaveFile to SidebarFile format for component props
 */
function convertToSidebarFile(waveFile: WaveFile): SidebarFile {
  let status: SidebarFile['status'] = 'success'
  
  if (waveFile.isLoading) {
    // Use processingStatus if available, otherwise default to 'parsing' for backward compatibility
    status = waveFile.processingStatus || 'parsing'
  } else if (waveFile.error) {
    status = 'error'
  }

  return {
    id: waveFile.id,
    filename: waveFile.name,
    status,
    fileType: waveFile.fileType,
    curves: waveFile.parsedData && 'curves' in waveFile.parsedData 
      ? waveFile.parsedData.curves?.map(curve => ({
          name: curve.name,
          yUnit: curve.yUnit || ''
        }))
      : undefined,
    error: waveFile.error
  }
}

/**
 * Main workspace content that uses all the contexts
 */
function WorkspaceContent() {
  const { projectId } = useParams<{ projectId: string }>()
  const { state: fileState, uploadFile, removeFile, removeAllFiles, parsedFiles, loadProject } = useFiles()
  const { chartConfigs, addChart, removeChart, setChartConfigs } = useChartConfig()
  const { 
    templates, 
    activeTemplate, 
    isLoading: templatesLoading,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    setActiveTemplate,
    markTemplateModified,
    clearUnsavedChanges,
    isErdosMiller,
    currentUserId
  } = useTemplates()
  const { 
    timeShiftConfig, 
    updateTimeShift
  } = useTimeShifts()
  const theme = useTheme()

  // Compute available device types from parsed files
  const availableDeviceTypes = useMemo(() => {
    const deviceTypes = new Set<ToolType>()
    parsedFiles.forEach(file => {
      if (file.parsedData?.toolType) {
        deviceTypes.add(file.parsedData.toolType)
      }
    })
    const result = Array.from(deviceTypes)
    console.log('🎯 Available device types for TimeShift:', result, 'from', parsedFiles.length, 'parsed files')
    return result
  }, [parsedFiles])

  const [activeTab, setActiveTab] = useState('files')
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [openCurveFormat, setOpenCurveFormat] = useState<{ chartId: string; curveId: string } | null>(null)

  // Load project files when component mounts
  useEffect(() => {
    if (projectId) {
      console.log('🔄 Loading project files for:', projectId)
      loadProject(projectId)
    }
  }, [projectId, loadProject])

  // Handle axis configuration changes 
  const handleAxisConfigChange = useCallback((chartId: string, axisId: YAxisPosition, config: YAxisConfig) => {
    const updatedConfigs = chartConfigs.map((chart: ChartConfig) => 
      chart.id === chartId 
        ? { 
            ...chart, 
            yAxes: {
              ...chart.yAxes,
              [axisId]: config
            }
          }
        : chart
    )
    setChartConfigs(updatedConfigs)
    markTemplateModified()
  }, [chartConfigs, setChartConfigs, markTemplateModified])



  // Handle curve format changes
  const handleCurveFormatChange = useCallback((chartId: string, curveId: string, format: CurveStyling) => {
    const updatedConfigs = chartConfigs.map((chart: ChartConfig) => 
      chart.id === chartId 
        ? { 
            ...chart, 
            curveReferences: chart.curveReferences.map(curveRef => {
              const currentCurveId = `${curveRef.reference.fileType}:${curveRef.reference.curveName}`
              return currentCurveId === curveId 
                ? { ...curveRef, styling: format }
                : curveRef
            })
          }
        : chart
    )
    setChartConfigs(updatedConfigs)
    markTemplateModified()
  }, [chartConfigs, setChartConfigs, markTemplateModified])

  // Handle drag and drop from curve explorer to charts
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    
    if (!over) return

    // Check if we're dropping a curve onto a chart
    const dragData = active.data.current
    const overData = over.data.current

    if (dragData?.type === 'curve-from-explorer' && overData?.type === 'chart') {
      // Identify target chart first
      const chartId = overData.chartId as string | undefined
      if (!chartId) return

      const targetChart = chartConfigs.find((c: ChartConfig) => c.id === chartId)

      // Build styled reference using helper (handles default color selection)
      const styledRef: StyledCurveReference = assignDefaultCurveStyling(
        targetChart,
        {
          fileType: dragData.curveReference.fileType,
          curveName: dragData.curveReference.curveName
        }
      )

      // Add curve to the chart configuration
      const updatedConfigs = chartConfigs.map((chart: ChartConfig) => 
        chart.id === chartId 
          ? { 
              ...chart, 
              curveReferences: [...chart.curveReferences, styledRef]
            }
          : chart
      )
      setChartConfigs(updatedConfigs)
      markTemplateModified()
    }
  }, [chartConfigs, setChartConfigs, markTemplateModified])

  // Handle curve move between axes
  const handleCurveMoveAxis = useCallback((
    chartId: string, 
    curveRef: StyledCurveReference, 
    fromAxis: YAxisPosition, 
    toAxis: YAxisPosition
  ) => {
    console.log('🎯 Moving curve between axes:', {
      curve: curveRef.reference.curveName,
      from: fromAxis,
      to: toAxis
    })
    
    const updatedConfigs = chartConfigs.map((chart: ChartConfig) => 
      chart.id === chartId 
        ? {
            ...chart,
            curveReferences: chart.curveReferences.map(ref => {
              if (ref.reference.fileType === curveRef.reference.fileType && 
                  ref.reference.curveName === curveRef.reference.curveName) {
                return {
                  ...ref,
                  styling: { ...ref.styling, yAxisId: toAxis }
                }
              }
              return ref
            })
          }
        : chart
    )
    setChartConfigs(updatedConfigs)
    markTemplateModified()
  }, [chartConfigs, setChartConfigs, markTemplateModified])

  // Handle curve removal from chart
  const handleCurveRemove = useCallback((chartId: string, curveId: string) => {
    console.log('🗑️ Removing curve from chart:', {
      chartId,
      curveId
    })
    
    const updatedConfigs = chartConfigs.map((chart: ChartConfig) => 
      chart.id === chartId 
        ? {
            ...chart,
            curveReferences: chart.curveReferences.filter(ref => {
              const refId = `${ref.reference.fileType}:${ref.reference.curveName}`
              return refId !== curveId
            })
          }
        : chart
    )
    setChartConfigs(updatedConfigs)
    markTemplateModified()
  }, [chartConfigs, setChartConfigs, markTemplateModified])

  // Handle curve reorder within axis
  const handleCurveReorder = useCallback((
    chartId: string,
    axisId: YAxisPosition,
    fromIndex: number,
    toIndex: number
  ) => {
    const updatedConfigs = chartConfigs.map((chart: ChartConfig) => {
      if (chart.id !== chartId) return chart

      // Group curves by axis
      const curvesByAxis: Record<string, StyledCurveReference[]> = {
        left: [],
        right: [],
        left2: [],
        right2: []
      }
      
      chart.curveReferences.forEach(curveRef => {
        const axis = curveRef.styling?.yAxisId || 'left'
        curvesByAxis[axis].push(curveRef)
      })
      
      // Reorder within the specific axis
      const axisCurves = [...curvesByAxis[axisId]]
      const [movedCurve] = axisCurves.splice(fromIndex, 1)
      axisCurves.splice(toIndex, 0, movedCurve)
      curvesByAxis[axisId] = axisCurves
      
      // Flatten back to single array
      const reorderedCurves = [
        ...curvesByAxis.left,
        ...curvesByAxis.right,
        ...curvesByAxis.left2,
        ...curvesByAxis.right2
      ]
      
      return {
        ...chart,
        curveReferences: reorderedCurves
      }
    })
    setChartConfigs(updatedConfigs)
    markTemplateModified()
  }, [chartConfigs, setChartConfigs, markTemplateModified])

  // Handle chart title change
  const handleChartTitleChange = useCallback((chartId: string, newTitle: string) => {
    const updatedConfigs = chartConfigs.map((chart: ChartConfig) => 
      chart.id === chartId 
        ? { ...chart, title: newTitle }
        : chart
    )
    setChartConfigs(updatedConfigs)
    markTemplateModified()
  }, [chartConfigs, setChartConfigs, markTemplateModified])

  // Handle charts change (wrapper for direct chart config updates)
  const handleChartsChange = useCallback((newChartConfigs: ChartConfig[]) => {
    setChartConfigs(newChartConfigs)
    markTemplateModified()
  }, [setChartConfigs, markTemplateModified])

  // Handle legend corner change (toolbar control in ChartContainer)
  const handleChartLegendCornerChange = useCallback((chartId: string, corner: 'tl' | 'tr' | 'bl' | 'br' | 'none') => {
    const updatedConfigs = chartConfigs.map((chart: ChartConfig) =>
      chart.id === chartId ? { ...chart, legendCorner: corner } : chart
    )
    setChartConfigs(updatedConfigs)
    markTemplateModified()
  }, [chartConfigs, setChartConfigs, markTemplateModified])

  // Handle legend mode change (overlay/bottom/none)
  const handleChartLegendModeChange = useCallback((chartId: string, mode: 'overlay' | 'bottom' | 'none') => {
    const updatedConfigs = chartConfigs.map((chart: ChartConfig) =>
      chart.id === chartId ? { ...chart, legendMode: mode } : chart
    )
    setChartConfigs(updatedConfigs)
    markTemplateModified()
  }, [chartConfigs, setChartConfigs, markTemplateModified])

  // Handle chart reordering via drag-and-drop in ChartConfigurator
  const handleChartsReorder = useCallback((fromIndex: number, toIndex: number) => {
    // Validate indices to prevent array corruption
    if (fromIndex < 0 || fromIndex >= chartConfigs.length || toIndex < 0 || toIndex >= chartConfigs.length) return
    
    // Reorder charts using splice to maintain array integrity
    const newCharts = [...chartConfigs]
    const [movedChart] = newCharts.splice(fromIndex, 1)
    newCharts.splice(toIndex, 0, movedChart)
    setChartConfigs(newCharts)
    markTemplateModified()
  }, [chartConfigs, setChartConfigs, markTemplateModified])

  // Handle add chart (wrapper to mark template as modified)
  const handleAddChart = useCallback(() => {
    addChart()
    markTemplateModified()
  }, [addChart, markTemplateModified])

  // Handle remove chart (wrapper to mark template as modified)
  const handleRemoveChart = useCallback((chartId: string) => {
    removeChart(chartId)
    markTemplateModified()
  }, [removeChart, markTemplateModified])

  // Legend click shortcut -> open curve format in configurator
  const handleLegendItemClick = useCallback((chartId: string, curveId: string) => {
    setActiveTab('charts')
    setSidebarCollapsed(false)
    setOpenCurveFormat({ chartId, curveId })
  }, [])

  // Handle chart height changes
  const handleChartHeightChange = useCallback((chartId: string, height: number) => {
    // Map height number back to heightPreset
    const heightToPreset: Record<number, ChartHeightPreset> = {
      250: 'small',
      400: 'medium', 
      550: 'large',
      700: 'xl'
    }
    
    const heightPreset = heightToPreset[height]
    if (!heightPreset) {
      console.warn('📏 Unknown height value:', height)
      return
    }
    
    // Update chart config with new height preset
    const updatedConfigs = chartConfigs.map((chart: ChartConfig) => 
      chart.id === chartId 
        ? { ...chart, heightPreset }
        : chart
    )
    setChartConfigs(updatedConfigs)
    
    markTemplateModified()
  }, [chartConfigs, setChartConfigs, markTemplateModified])

  // Legend toggling now happens locally in ChartContainer using opacity (no persistence)

  // Template handlers
  const handleTemplateChange = useCallback((templateId: string | undefined) => {
    console.log('🔄 WORKSPACE: Template change requested:', templateId)
    console.log('🔄 WORKSPACE: Current templates:', templates.length, 'available:', templates.map(t => t.id))
    console.log('🔄 WORKSPACE: Current activeTemplate:', activeTemplate.activeTemplateId)
    
    if (!templateId) {
      // Clear template
      console.log('🔄 WORKSPACE: Clearing template')
      setActiveTemplate(undefined)
      return
    }
    
    // Find template in loaded templates
    const template = templates.find(t => t.id === templateId)
    if (!template) {
      console.error('❌ WORKSPACE: Template not found:', templateId, 'in', templates.map(t => t.id))
      return
    }
    
    console.log('✅ WORKSPACE: Found template:', template.name)
    console.log('📊 WORKSPACE: Template chart configs count:', template.chartConfigs.length)
    
    // Apply template chart configurations
    setChartConfigs(template.chartConfigs)
    
    // Set active template and clear unsaved changes
    setActiveTemplate(templateId)
    clearUnsavedChanges()
    
    console.log('🎯 WORKSPACE: Template applied successfully')
  }, [setActiveTemplate, templates, setChartConfigs, clearUnsavedChanges, activeTemplate.activeTemplateId])

  const handleSaveTemplate = useCallback(async () => {
    if (activeTemplate.activeTemplateId) {
      await updateTemplate(activeTemplate.activeTemplateId, chartConfigs)
    }
  }, [activeTemplate.activeTemplateId, updateTemplate, chartConfigs])

  const handleSaveAsNewTemplate = useCallback(async (newName: string) => {
    try {
      console.log('WORKSPACE: Creating new template:', newName)
      console.log('WORKSPACE: Chart configs count:', chartConfigs.length)
      console.log('WORKSPACE: Current user ID:', currentUserId)
      console.log('WORKSPACE: Is Erdos Miller:', isErdosMiller)
      
      await createTemplate(newName, chartConfigs)
      console.log('WORKSPACE: Template created successfully')
    } catch (error) {
      console.error('WORKSPACE: Failed to create template:', error)
      // Re-throw to let the UI handle the error
      throw error
    }
  }, [createTemplate, chartConfigs, currentUserId, isErdosMiller])

  const handleDeleteTemplate = useCallback(async (templateId: string) => {
    await deleteTemplate(templateId)
  }, [deleteTemplate])

  return (
    <DndContext onDragEnd={handleDragEnd}>
      <Box sx={{ 
        display: 'flex', 
        height: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' }, // Account for Header height
        overflow: 'hidden' 
      }}>
        {/* Sidebar with tabs */}
        <WaveSidebar 
          activeTab={activeTab}
          collapsed={sidebarCollapsed}
          width={525}
          onTabChange={setActiveTab}
          onCollapsedChange={setSidebarCollapsed}
        >
          <WaveSidebar.Tab id="files" icon={<FolderOpenIcon />} label="Files">
            <FileExplorer 
              files={fileState.files.map(convertToSidebarFile)}
              onFileUpload={uploadFile}
              onFileDelete={removeFile}
              onDeleteAll={removeAllFiles}
            />
          </WaveSidebar.Tab>
          
          <WaveSidebar.Tab id="data" icon={<DataUsageIcon />} label="Data">
            <CurveExplorer 
              files={parsedFiles.map(convertToSidebarFile)}
            />
          </WaveSidebar.Tab>
          
          <WaveSidebar.Tab id="charts" icon={<BarChartIcon />} label="Charts">
            <ChartConfigurator 
              charts={chartConfigs}
              openCurveFormat={openCurveFormat}
              onChartsChange={handleChartsChange}
              onAddChart={handleAddChart}
              onDeleteChart={handleRemoveChart}
              onAxisConfigChange={handleAxisConfigChange}
              onCurveFormatChange={handleCurveFormatChange}
              onCurveMoveAxis={handleCurveMoveAxis}
              onCurveReorder={handleCurveReorder}
              onCurveRemove={handleCurveRemove}
              onChartTitleChange={handleChartTitleChange}
              onChartsReorder={handleChartsReorder}
            />
          </WaveSidebar.Tab>
          
          <WaveSidebar.Tab id="time" icon={<AccessTimeIcon />} label="Time">
            <TimeShiftPanel 
              config={timeShiftConfig}
              availableDevices={availableDeviceTypes}
              onTimeShiftChange={updateTimeShift}
              theme={theme.palette.mode}
            />
          </WaveSidebar.Tab>
        </WaveSidebar>
        
        {/* Main content area */}
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'hidden',
          minHeight: 0  // Critical for nested flex containers
        }}>
          {/* Header with template dropdown */}
          <Box sx={{ 
            p: 2, 
            borderBottom: 1, 
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            <TemplateDropdown
              templates={templates}
              activeTemplate={activeTemplate}
              loading={templatesLoading}
              currentUserId={currentUserId || ''}
              isErdosMiller={isErdosMiller}
              onTemplateChange={handleTemplateChange}
              onSaveTemplate={handleSaveTemplate}
              onSaveAsNewTemplate={handleSaveAsNewTemplate}
              onDeleteTemplate={handleDeleteTemplate}
            />
          </Box>
          
          {/* Chart canvas */}
          <Box sx={{ 
            flex: 1, 
            overflow: 'auto',
            minHeight: 0  // Critical for flex containers with overflow
          }}>
            <ChartDataResolver 
              chartConfigs={chartConfigs} 
              timeShiftConfig={timeShiftConfig}
            >
              {(resolvedCharts) => (
                <WaveChartCanvas 
                  charts={resolvedCharts}
                  onLegendCornerChange={handleChartLegendCornerChange}
                  onLegendModeChange={handleChartLegendModeChange}
                  onAddChart={handleAddChart}
                  onChartClose={handleRemoveChart}
                  onChartHeightChange={handleChartHeightChange}
                  onLegendItemClick={handleLegendItemClick}
                />
              )}
            </ChartDataResolver>
          </Box>
        </Box>
      </Box>
    </DndContext>
  )
}

/**
 * Workspace page with all context providers
 */
interface WorkspacePageProps {
  onProjectNameChange?: (name: string | null) => void
}

export function WorkspacePage({ onProjectNameChange }: WorkspacePageProps) {
  const { projectId } = useParams<{ projectId: string }>()

  if (!projectId) {
    return <div>Project ID is required</div>
  }

  return (
    <FileProvider onProjectNameChange={onProjectNameChange}>
      <ChartConfigProvider>
        <TemplateProvider>
          <TimeShiftProvider projectId={projectId}>
            <WorkspaceContent />
          </TimeShiftProvider>
        </TemplateProvider>
      </ChartConfigProvider>
    </FileProvider>
  )
}

export default WorkspacePage
